import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SortingRule } from 'react-table';
import { Col, Row } from 'reactstrap';
import { useAvailableModulesContext } from '../../context/AvailableModulesContext';
import Toast from "../../helpers/Toast";
import { AddPartToAssistanceDTO } from '../../models/Assistance/AddPartToAssistanceDTO';
import { AssistancePartRequestWarehouse } from '../../models/AssistanceParts/AssistancePartRequestWarehouseDTO';
import { Parameter } from '../../models/Parameter';
import { PartDTO } from '../../models/Parts/PartDTO';
import { PartListDTO } from "../../models/Parts/PartListDTO";
import { AssistanceService } from '../../services/AssistanceService';
import { PartService } from '../../services/PartService';
import ButtonComponent from '../ButtonComponent';
import InputComponent from '../InputComponent';
import { useMobile } from '../Layout/UseResponsive';
import TableComponent from '../TableComponent';
import { PartCard } from './PartCard';
import LoadingSpinner from '../LoadingSpinner';

export type PartsTableComponentType = {
    assistanceId: number;
    reloadParts: boolean;
    querySearch: string;
    setGoToPage: React.Dispatch<React.SetStateAction<number>>;
    goToPage: number;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    currentPage: number;
    setReload: React.Dispatch<React.SetStateAction<boolean>>;
    reload: boolean;
    setActiveTab: React.Dispatch<React.SetStateAction<number>>;
    isComplete: boolean;
    isAcceptedBudget: boolean;
    assistanceStatus: string;
    searchByNameOnly?: boolean;
}


const PartsTableComponent = ({ assistanceId, reloadParts, querySearch, setGoToPage, goToPage, setCurrentPage, currentPage, setReload, reload, setActiveTab, isComplete, isAcceptedBudget, assistanceStatus, searchByNameOnly = false }: PartsTableComponentType) => {
    const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
    const partService = new PartService();
    const isMobile = useMobile();
    const [parts, setParts] = useState<PartDTO[]>([]);
    const [selectedPart, setSelectedPart] = useState<PartListDTO>();
    const [totalPages, setTotalPages] = useState<number>(0);
    const pageSizes: number[] = [5, 15, 30];
    const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
    const [isAddButtonLoading, setIsAddButtonLoading] = useState<boolean>(false);
    const intl = useIntl()
    const assistanceService = new AssistanceService();
    const [quantity, setQuantity] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(5);
    const [initialSortBy] = useState<SortingRule<object>[]>([]);
    const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);
    const [hasSearched, setHasSearched] = useState<boolean>(false);
    const { availableModules } = useAvailableModulesContext();
    const [isActiveWarehouseModule, setIsActiveWarehouseModule] = useState<boolean>(false);
    const isLocked = isAcceptedBudget || isComplete;

    const searchParts = async (currentPage: number, pageSize: number, sortBy: SortingRule<object>[] | null) => {
        if (!querySearch || querySearch.trim() === "") {
            setParts([]);
            setTotalPages(0);
            setHasSearched(false);
            return;
        }

        setIsLoadingTable(true);
        setHasSearched(true);

        var Parameter: Parameter[] = [];
        const paramName = searchByNameOnly ? "name" : "part";
        Parameter.push({ name: paramName, value: querySearch } as Parameter)

        var SortParameter: Parameter[] = [];
        sortBy?.forEach(element => {
            SortParameter.push({ name: element.id.toString(), value: (element.desc === true ? "DESC" : "ASC") } as Parameter)
        });

        var response = await partService.GetAll(Parameter, SortParameter, currentPage, pageSize);

        if (response.success === false) {
            setIsLoadingTable(false);
            setGoToPage(1);
            Toast.Show("error", response.message);
            return;
        }

        setCurrentPage(currentPage);
        setPageSize(pageSize);
        setTotalPages(response.totalPages);
        setIsLoadingTable(false);
        setParts(response.items);
        setCurrentSorting(sortBy);
        setGoToPage(0);

        if (response.totalPages > 0 && currentPage > response.totalPages) {
            setGoToPage(1);
        }
    }

    const SelectPartToWarehouse = async () => {

        if (assistanceId === 0) {
            return;
        }
        var data: AssistancePartRequestWarehouse = {
            quantity: quantity,
            partErpId: selectedPart?.erpId === undefined ? "" : selectedPart.erpId,
            PartDescription: selectedPart?.requestPartDescription === undefined ? "" : selectedPart.requestPartDescription,
            billable: true,
        }
        setIsButtonLoading(true);
        var response = await assistanceService.SelectPartToWarehouse(assistanceId, data);

        if (response.success === false) {
            Toast.Show("error", response.message);
            setIsButtonLoading(false);
            return;
        }

        setIsButtonLoading(false);
        setReload(!reload);
        setActiveTab(2);
        setQuantity(0);
        Toast.Show("success", "Peça selecionada com sucesso");
    }

    const AddPartToAssistance = async () => {
        var data: AddPartToAssistanceDTO = {
            assistanceId: assistanceId,
            quantity: quantity,
            billable: true,
            requestPartDescription: selectedPart?.requestPartDescription === undefined ? "" : selectedPart.requestPartDescription,
        };

        setIsAddButtonLoading(true);

        // encode devido ao url ser /id/... e o id da peça puder ser taptip6/25 --» parte a chamada ao backend
        const encodedSelectedPartErpId = encodeURIComponent(selectedPart?.erpId || '');

        var response = await assistanceService.AddPartToAssistance(data, encodedSelectedPartErpId);

        if (response.success === false) {
            setIsAddButtonLoading(false);
            Toast.Show("error", response.message);
            return;
        }

        setIsAddButtonLoading(false);
        setReload(!reload);
        setActiveTab(0);
        setQuantity(0);
        Toast.Show("success", response.message);
    }

    const ClearBoxes = () => {
        setSelectedPart({ erpId: "", name: "", requestPartDescription: "" } as PartListDTO);
        setQuantity(0);
    }

    const columns = React.useMemo(
        () => [
            {
                Header: <FormattedMessage id="Screens.Assistances.Parts.Table.Code" />,
                accessor: "erpId",
                width: 35,
            },
            {
                Header: <FormattedMessage id="Screens.Assistances.Parts.Table.Name" />,
                accessor: "name",
                width: 35,
            },
            {
                Header: <FormattedMessage id="Screens.Assistances.Parts.Table.PartToSelect" />,
                accessor: " ",
                Cell: (props: any) => {
                    return (
                        <div style={{ display: "inline-flex", height: "30px" }}>
                            <ButtonComponent
                                text={intl.formatMessage({ id: "PartsTableComponent.Select" })}
                                disabled={isLocked}
                                onClick={() => {
                                    setSelectedPart(props.cell.row.original)
                                }} />
                        </div>
                    );
                },
                width: 30,
            },
        ],
        [isLocked],
    );

    useEffect(() => {
        searchParts(currentPage, pageSize, currentSorting);
    }, [reloadParts]);

    useEffect(() => {
        // Trigger search when page or page size changes (for mobile pagination)
        if (hasSearched) {
            searchParts(currentPage, pageSize, currentSorting);
        }
    }, [currentPage, pageSize]);

    useEffect(() => {
        if (availableModules.includes("IsActiveWarehouseModule")) {
            setIsActiveWarehouseModule(true);
        }
    }, [availableModules]);

    return (
        <div>
            {hasSearched && (
                <Row style={{ marginBottom: "1rem" }}>
                    {isMobile ? (
                        <>
                            <div className="parts-card-grid">
                                {isLoadingTable ? (
                                    <LoadingSpinner />
                                ) : parts.length > 0 ? (
                                    parts.map((part, index) => (
                                        <PartCard
                                            key={index}
                                            part={part}
                                            onSelect={() => setSelectedPart({
                                                erpId: part.erpId,
                                                name: part.name,
                                                quantity: 0,
                                                erpLine: null,
                                                date: null,
                                                requestPartDescription: '',
                                                billable: true
                                            })}
                                            disabled={isLocked}
                                            isSelected={selectedPart?.erpId === part.erpId}
                                        />
                                    ))
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                                        <FormattedMessage id="NoResults" />
                                    </div>
                                )}
                            </div>
                            {totalPages > 1 && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '1rem 0.5rem',
                                    opacity: isLoadingTable ? 0.5 : 1,
                                    width: '100%'
                                }}>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage <= 1 || isLoadingTable}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: currentPage > 1 && !isLoadingTable ? '#007bff' : '#ccc',
                                            cursor: currentPage > 1 && !isLoadingTable ? 'pointer' : 'not-allowed',
                                            fontSize: '18px',
                                            padding: '4px 8px'
                                        }}
                                    >
                                        &lt;
                                    </button>

                                    <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>
                                        Página {currentPage} de {totalPages}
                                    </span>

                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage >= totalPages || isLoadingTable}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: currentPage < totalPages && !isLoadingTable ? '#007bff' : '#ccc',
                                            cursor: currentPage < totalPages && !isLoadingTable ? 'pointer' : 'not-allowed',
                                            fontSize: '18px',
                                            padding: '4px 8px'
                                        }}
                                    >
                                        &gt;
                                    </button>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto', paddingLeft: '12px' }}>
                                        <span style={{ fontSize: '13px', color: '#666', whiteSpace: 'nowrap' }}>Por página:</span>
                                        <select
                                            disabled={isLoadingTable}
                                            value={pageSize}
                                            onChange={(e) => setPageSize(Number(e.target.value))}
                                            style={{
                                                padding: '4px 8px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                fontSize: '14px',
                                                cursor: isLoadingTable ? 'not-allowed' : 'pointer',
                                                backgroundColor: 'white'
                                            }}
                                        >
                                            {pageSizes.map((size) => (
                                                <option key={size} value={size}>
                                                    {size}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <TableComponent
                            goToPage={goToPage}
                            loading={isLoadingTable}
                            pageCount={totalPages}
                            columns={columns}
                            manualPagination={true}
                            fetchData={searchParts}
                            pageSizes={pageSizes}
                            initialPage={currentPage}
                            initialPageSize={pageSize}
                            initialSortBy={initialSortBy}
                            data={parts}
                            dontOrderLastCollumn={true}
                            dontOrderFirstCollumn={false}
                        />
                    )}
                </Row>
            )}
            <Row xl={12} lg={12} md={12} sm={12} xs={12}>
                <Col xl={9} lg={9} md={9} sm={12} xs={12}>
                    <Row xl={12} lg={12} md={9} sm={12} xs={12}>
                        <InputComponent xl={4} lg={4} md={5} sm={12} xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            value={selectedPart?.erpId}
                            label={intl.formatMessage({ id: "Screens.Assistances.Parts.Label.Code" })}
                            readOnly={true}

                        />
                        <InputComponent xl={4} lg={4} md={5} sm={12} xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            value={selectedPart?.name}
                            label={intl.formatMessage({ id: "Screens.Assistances.Parts.Label.Name" })}
                            readOnly={isLocked}
                            onChange={(e: string) =>
                                selectedPart !== undefined &&
                                setSelectedPart({ ...selectedPart, name: e })}
                        />
                        <InputComponent xl={4} lg={4} md={2} sm={12} xs={12}
                            style={{ width: "100%" }}
                            type={"number"}
                            value={quantity}
                            label={intl.formatMessage({ id: "Screens.Assistances.Parts.Label.Quantity" })}
                            readOnly={isLocked}
                            onChange={(e: number) => setQuantity(e)}
                            clearZeroOnFocus={true}
                        />
                    </Row>
                    <Row xl={12} lg={12} md={12} sm={12} xs={12}>
                        <InputComponent xl={12} lg={12} md={12} sm={12} xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            value={selectedPart?.requestPartDescription}
                            label={intl.formatMessage({ id: "Screens.Assistances.Parts.Label.Description" })}
                            readOnly={isLocked}
                            onChange={(e: string) =>
                                selectedPart !== undefined &&
                                setSelectedPart({ ...selectedPart, requestPartDescription: e })}
                        />
                    </Row>
                </Col>
                <Col xl={3} lg={3} md={3} sm={12} xs={12}>
                    <ButtonComponent
                        className="hide-on-mobile"
                        style={{ width: '100%', marginTop: '30px', }}
                        text={intl.formatMessage({ id: "Screens.Assistances.Parts.Label.ClearBoxes", defaultMessage: "Limpar Form" })}
                        onClick={() => ClearBoxes()}
                        disabled={isLocked}
                    />
                    {isActiveWarehouseModule && (
                        <ButtonComponent
                            loading={isButtonLoading}
                            style={{ width: '100%', marginTop: '3px', }}
                            text={intl.formatMessage({ id: "Screens.Assistances.Parts.Navbar.PartsNewRequest", defaultMessage: "Adicionar ao pedido" })}
                            onClick={() => SelectPartToWarehouse()}
                            disabled={isLocked}
                        />
                    )}
                    <ButtonComponent
                        loading={isAddButtonLoading}
                        style={{ width: '100%', marginTop: '3px', marginBottom: '30px' }}
                        text={intl.formatMessage({ id: "Screens.Assistances.Parts.Label.Add", defaultMessage: "Adicionar Assist" })}
                        onClick={() => AddPartToAssistance()}
                        disabled={isLocked}
                    />
                </Col>
            </Row>
        </div>
    )
}

export default PartsTableComponent